import { useState } from 'react';
import PropTypes from 'prop-types';
import { Eye, EyeOff, HelpCircle } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Input } from '#/modules/commonComponents/form/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

import { getNestedError, hasRegisterFn, noop } from './formFieldUtils';

const FloatingInput = ({
  id,
  type,
  label,
  tip,
  value = '',
  register = noop,
  errors,
  onChange = noop,
  onInput = noop,
  onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  },
  inputClassName = '',
  containerClassName = '',
  isVisible = false,
  readOnly = false,
  autoComplete,
  /** Floating label 預設空白；type=date 等可傳格式提示（瀏覽器未必顯示） */
  placeholder = ' ',
  /** 為 true 時不渲染 .form_error 區塊（BOM 群組／位置等改走 FormValidationSummary） */
  suppressInlineError = false,
  /** type=number 時為 true 則允許滾輪改值（預設擋滾輪） */
  allowWheelChange = false,
  onBlur,
  min,
  max,
  step,
}) => {
  const [visibleSwitch, setVisibleSwitch] = useState(isVisible);
  const hasRegister = hasRegisterFn(register);
  const registerProps = hasRegister ? register(id) : {};
  const isControlled = !hasRegister && value !== undefined && value !== null && onChange !== noop;
  const isReadOnly = !hasRegister && value !== undefined && value !== null && onChange === noop;
  const fieldError = getNestedError(errors, id);
  const resolvedType = type === 'password' && isVisible
    ? (visibleSwitch ? type : 'text')
    : type;

  function handleBlur(event) {
    registerProps.onBlur?.(event);
    onBlur?.(event);
  }

  const controlProps = hasRegister
    ? { ...registerProps, id, readOnly, onBlur: onBlur || registerProps.onBlur ? handleBlur : undefined }
    : isControlled
      ? { id, value: value || '', onChange, readOnly, onBlur }
      : isReadOnly
        ? { id, value: value || '', readOnly: readOnly !== false, onBlur }
        : { id, defaultValue: value || '', onChange, readOnly, onBlur };

  return (
    <div className={cn('input_container', containerClassName)}>
      <div className="relative">
        <Input
          className={cn(inputClassName, readOnly && 'readonly')}
          type={resolvedType}
          placeholder={placeholder}
          aria-invalid={fieldError ? true : undefined}
          autoComplete={autoComplete}
          onInput={onInput}
          onKeyDown={onKeyDown}
          allowWheelChange={allowWheelChange}
          {...controlProps}
          min={min}
          max={max}
          step={step}
        />
        <label htmlFor={id} className="input_label">
          {tip ? (
            <span className="inline-flex items-center gap-0.5">
              <span>{label}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="
                      text-gray-400 inline-flex cursor-help items-center border-0 bg-transparent p-0
                      hover:text-primary-600
                    "
                    aria-label={`${label}說明`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    <HelpCircle size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {tip}
                </TooltipContent>
              </Tooltip>
            </span>
          ) : (
            label
          )}
        </label>
        {isVisible && type === 'password' ? (
          <button
            type="button"
            className="isVisible"
            aria-label={visibleSwitch ? '隱藏密碼' : '顯示密碼'}
            onClick={() => setVisibleSwitch((prev) => !prev)}
          >
            {visibleSwitch ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        ) : null}
      </div>
      {!suppressInlineError ? (
        <p className="form_error">
          {fieldError ? fieldError.message || null : null}
        </p>
      ) : null}
    </div>
  );
};

FloatingInput.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  register: PropTypes.func,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onKeyDown: PropTypes.func,
  containerClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  isVisible: PropTypes.bool,
  readOnly: PropTypes.bool,
  autoComplete: PropTypes.string,
  placeholder: PropTypes.string,
  suppressInlineError: PropTypes.bool,
  allowWheelChange: PropTypes.bool,
  onBlur: PropTypes.func,
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default FloatingInput;
